import { ARFunction, SeriesFunction, ValueFunction } from '../Functions';
import { InputMap } from './InputMap';
import { TimeSeries, FixedSeries } from '../DataSeries';

const _ = require('lodash');


/**
 * 
 */
export class Component {

  /**
   * Initialize a new empty Component
   * 
   * @param {String} label Label for the Component
   */
  constructor(label) {
    this.label = label;
    this.data = null;
    this.method = null;
    this.inputMapping = new InputMap();
    this.scenarios = [];
  }


  /**
   * Assign a raw input TimeSeries to the component and void any dynamic calculation
   * settings previously assigned
   * 
   * @param {TimeSeries|FixedSeries} rawData Raw input time series to assign to the component
   */
  setData(rawData) {
    this.data = rawData;
    this.method = null;
    this.inputMapping = new InputMap();
    this.scenarios = [];
  }

  
  /**
   * Load any required data for this Component
   */
  loadData() {
    if (this.data && this.data.isRemoteSeries) {
      return this.data.loadRemoteData();
    } else {
      return Promise.resolve(false);
    }
  }


  /**
   * Update the order to use in recalculating the inputs
   * 
   * @param {Object} newInputMap Mapping method inputs to component names
   */
  updateInputsMapping(newInputMap) {
    this.inputMapping = new InputMap(newInputMap);
  }


  /**
   * 
   * @param {String} oldLabel Label for the old component
   * @param {String} newLabel Label for the new component
   */
  swapComponentInput(oldLabel, newLabel) {
    this.inputMapping.swapInput(oldLabel, newLabel);
  }

  
  /**
   * Update the calculation method for this components data based on the provided input
   * and input ordering
   * 
   * @param {Function} newMethod Method to apply on the inputs for this components data
   * @param {String[]} inputMapping Mapping of inputs to the method parameters
   */
  updateMethod(newMethod, inputMapping = {}) {
    this.method = newMethod;
    
    let defaultInput = this.method.getDefaultParameterMap();
    this.inputMapping = new InputMap(defaultInput, inputMapping);
    this.scenarios = [];
  }



  /**
   * Add another scenario to the component
   * 
   * @param {Object} scenario Alternative input map to use
   */
  addScenario(scenario) {
    let inputScenario = new InputMap(scenario);
    this.scenarios.push(inputScenario);
  }


  /**
   * Remove a scenario from the component
   * 
   * @param {Number} scenarioIndex Index of the scenario to remove
   */
  removeScenario(scenarioIndex) {
    _.pullAt(this.scenarios, scenarioIndex);
  }


  /**
   * 
   * @param {String} input Input label to apply a lag to
   * @param {Number} lag Number of indices to lag
   */
  setInputLag(input, lag) {
    if (!this.inputMapping.includes(input))
      throw Error(`Variable ${input} is not currently an input`);
    
    this.inputLags[input] = lag;
  }


  /**
   * Convert an array of components into a {component.label: component.data} mapping
   * 
   * @param {Component[]} componentArray Array of components to convert
   */
  createLabelToDataMap(componentArray) {
    return componentArray.reduce((arr, c) => {
      arr[c.label] = c.data;
      return arr;
    }, {});
  }


  /**
   * Recalculate the component using the provided components and scenario
   * 
   * @param {Component[]} componentArray Array of components to use as inputs
   * @param {Number} scenarioIndex Index of the scenario to use in the recalculation
   */
  recalculate(componentArray) { //, scenarioIndex = 0) {
    if (!this.method) { return this.data; }

    // Convert the components into a {component.label: component.data} mapping
    let componentMap = this.createLabelToDataMap(componentArray);

    // Create the inputs to pass into the method
    let methodInput = this.inputMapping.fillInputs(componentMap);
    
    // Prepare and merge the data
    if (this.method instanceof ValueFunction && componentArray.some(c => c.data instanceof TimeSeries)) {
      this.data = this.method.applyToTimeSeries(methodInput);
    } else if (this.method instanceof ARFunction) {
      this.data = this.method.applyIteratively(methodInput);
    } else if (this.method instanceof SeriesFunction) {
      this.data = this.method(methodInput);
    } else {
      this.data = new FixedSeries(this.method(methodInput));
    }

    return this.data;
  }
}